import { ApiError, formatMoney, queryKeys, type StoreCreditSummary } from '@mevaser/shared'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

const KIND_LABEL: Record<string, string> = {
  adjust: 'Adjusted by staff',
  redeem: 'Spent',
  release: 'Returned (order not paid)',
  refund: 'Refunded',
}

/**
 * A customer's store credit (t1102): what they have, where it came from and
 * went, and — for staff allowed to — a way to add to it or take from it. Every
 * change needs a reason and stays in the history.
 */
export function StoreCreditCard({
  customerId,
  canAdjust,
}: {
  customerId: string
  canAdjust: boolean
}) {
  const [adjusting, setAdjusting] = useState(false)

  const { data } = useQuery({
    queryKey: queryKeys.storeCredit(customerId),
    queryFn: () => api.get<StoreCreditSummary>(`/customers/${customerId}/credit`),
  })

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Store credit</CardTitle>
        {canAdjust ? (
          <Button variant="secondary" size="sm" onClick={() => setAdjusting(true)}>
            Adjust
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="text-2xl font-semibold tabular">{formatMoney(data?.balanceCents ?? 0)}</div>
        {data && data.transactions.length > 0 ? (
          <ul className="divide-y divide-border">
            {data.transactions.slice(0, 8).map((t) => (
              <li key={t.id} className="flex items-start justify-between gap-3 py-2">
                <div>
                  <div>{KIND_LABEL[t.kind] ?? t.kind}</div>
                  <div className="text-xs text-foreground-muted">
                    {new Date(t.createdAt).toLocaleDateString()}
                    {t.orderId && t.orderNumber ? (
                      <>
                        {' · '}
                        <Link
                          to="/orders/$orderId"
                          params={{ orderId: t.orderId }}
                          className="text-primary hover:underline"
                        >
                          {t.orderNumber}
                        </Link>
                      </>
                    ) : null}
                    {t.note ? ` · ${t.note}` : ''}
                  </div>
                </div>
                <span className={`tabular ${t.amountCents < 0 ? 'text-danger' : 'text-success'}`}>
                  {t.amountCents < 0 ? '−' : '+'}
                  {formatMoney(Math.abs(t.amountCents))}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-foreground-muted">No store credit has been added yet.</p>
        )}
      </CardContent>
      <AdjustStoreCreditDialog
        customerId={customerId}
        balanceCents={data?.balanceCents ?? 0}
        open={adjusting}
        onOpenChange={setAdjusting}
      />
    </Card>
  )
}

function AdjustStoreCreditDialog({
  customerId,
  balanceCents,
  open,
  onOpenChange,
}: {
  customerId: string
  balanceCents: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [direction, setDirection] = useState<'add' | 'remove'>('add')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setDirection('add')
    setAmount('')
    setReason('')
    setError(null)
  }, [open])

  const adjust = useMutation({
    mutationFn: async () => {
      const dollars = Number(amount)
      if (!amount.trim() || !Number.isFinite(dollars) || dollars <= 0) {
        throw new Error('Enter the amount in dollars')
      }
      if (reason.trim().length < 3) throw new Error('Say why the balance is changing')
      const cents = Math.round(dollars * 100)
      return api.post(`/customers/${customerId}/credit/adjust`, {
        amountCents: direction === 'add' ? cents : -cents,
        reason: reason.trim(),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeCredit(customerId) })
      toast.success('Store credit updated')
      onOpenChange(false)
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : e.message),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust store credit</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            Current balance {formatMoney(balanceCents)}. The customer can spend it at checkout on
            the website or the kiosk, and at the counter.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Change" required>
              {(props) => (
                <Select
                  value={direction}
                  onValueChange={(v) => setDirection(v as 'add' | 'remove')}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add credit</SelectItem>
                    <SelectItem value="remove">Take credit away</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Amount" required hint="In dollars.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="10.00"
                />
              )}
            </Field>
          </div>
          <Field label="Reason" required hint="Kept with the change in the credit history.">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={adjust.isPending} onClick={() => adjust.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
